<!-- Header -->
<div class="flex items-center justify-between gap-4 px-6 py-4 bg-neutral-900 text-white">
  <div class="flex items-center gap-3">
    <span class="h-10 w-10 rounded-xl bg-amber-400 text-neutral-900 flex items-center justify-center">
      <mat-icon>{{ data.isEdit ? 'edit_location_alt' : 'add_location_alt' }}</mat-icon>
    </span>
    <div>
      <h1 class="font-bold text-xl leading-tight">{{ data.isEdit ? 'Edit address' : 'Add a new address' }}</h1>
      <p class="text-xs text-white/60">{{ data.isEdit ? 'Change the details and save' : 'Find it by postcode, or type it in' }}</p>
    </div>
  </div>
  <button type="button" (click)="dialogRef.close({ status: false })" class="h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors" matTooltip="Close" aria-label="Close">
    <mat-icon class="!text-[20px] !h-5 !w-5">close</mat-icon>
  </button>
</div>

<mat-dialog-content class="mat-typography !p-0">
  <form [formGroup]="createAddressForm" (ngSubmit)="onSubmit()">
    <div class="px-6 py-5 flex flex-col gap-6">

      <!-- Find by postcode -->
      <div class="rounded-2xl border border-amber-200 bg-amber-50/60 p-4">
        <div class="flex items-center gap-2 mb-2">
          <mat-icon class="!text-amber-500 !text-[20px] !h-5 !w-5">travel_explore</mat-icon>
          <span class="text-sm font-semibold text-gray-900">Find the address by postcode</span>
        </div>
        <app-loquate-finder (addressSelected)="onAddressSelected($event)" [addressType]="'billing'"></app-loquate-finder>
        <p class="text-xs text-gray-500 mt-2">Pick a match and the address fields below fill in. Name, phone and email are kept.</p>
      </div>

      <!-- Address type -->
      <div>
        <div class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">This is a</div>
        <div class="grid grid-cols-2 gap-3">
          <label class="flex items-center gap-3 rounded-xl border-2 px-4 py-3 transition-all"
            [ngClass]="[createAddressForm.get('addressType')?.value === 'BILLING' ? 'border-amber-400 bg-amber-50 ring-2 ring-amber-100' : 'border-gray-200', data.isEdit ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer hover:border-gray-300']">
            <input type="radio" class="h-4 w-4 accent-amber-500" value="BILLING" [attr.disabled]="data.isEdit ? true : null" formControlName="addressType" />
            <mat-icon class="!text-gray-400">receipt_long</mat-icon>
            <span><span class="block font-semibold text-gray-900 text-sm">Billing address</span><span class="block text-xs text-gray-500">Where the invoice goes</span></span>
          </label>
          <label class="flex items-center gap-3 rounded-xl border-2 px-4 py-3 transition-all"
            [ngClass]="[createAddressForm.get('addressType')?.value === 'SHIPPING' ? 'border-amber-400 bg-amber-50 ring-2 ring-amber-100' : 'border-gray-200', data.isEdit ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer hover:border-gray-300']">
            <input type="radio" class="h-4 w-4 accent-amber-500" value="SHIPPING" [attr.disabled]="data.isEdit ? true : null" formControlName="addressType" />
            <mat-icon class="!text-gray-400">local_shipping</mat-icon>
            <span><span class="block font-semibold text-gray-900 text-sm">Delivery address</span><span class="block text-xs text-gray-500">Where the goods are sent</span></span>
          </label>
        </div>
      </div>

      <!-- Contact -->
      <div>
        <div class="flex items-center gap-2 mb-3">
          <mat-icon class="!text-gray-400 !text-[20px] !h-5 !w-5">person</mat-icon>
          <span class="font-semibold text-gray-900">Contact</span>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4">
        <div>
          <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">First name *</label>
          <input [ngClass]="inputCls('firstName')" name="del-first-name" type="text" placeholder="First name" formControlName="firstName" trimInput />
          @if (f['firstName'].invalid && (f['firstName'].dirty || f['firstName'].touched)) {
          <div class="mt-1 text-xs text-red-500 flex flex-col">
            @if (f['firstName'].errors && f['firstName'].errors['required']) {
            <span>First name is required</span>
            }
            @if (f['firstName'].errors && f['firstName'].errors['minlength']) {
            <span>At least 2 characters</span>
            }
            @if (f['firstName'].errors && f['firstName'].errors['maxlength']) {
            <span>No more than 100 characters</span>
            }
          </div>
          }
        </div>
        <div>
          <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Last name *</label>
          <input [ngClass]="inputCls('lastName')" name="del-last-name" type="text" placeholder="Last name" formControlName="lastName" trimInput />
          @if (f['lastName'].invalid && (f['lastName'].dirty || f['lastName'].touched)) {
          <div class="mt-1 text-xs text-red-500 flex flex-col">
            @if (f['lastName'].errors && f['lastName'].errors['required']) {
            <span>Last name is required</span>
            }
            @if (f['lastName'].errors && f['lastName'].errors['minlength']) {
            <span>At least 2 characters</span>
            }
            @if (f['lastName'].errors && f['lastName'].errors['maxlength']) {
            <span>No more than 200 characters</span>
            }
          </div>
          }
        </div>
        <div>
          <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Phone number *</label>
          <input [ngClass]="inputCls('phoneNo')" name="del-phone-number" type="tel" placeholder="e.g. 07700900123" formControlName="phoneNo" trimInput />
          @if (f['phoneNo'].invalid && (f['phoneNo'].dirty || f['phoneNo'].touched)) {
          <div class="mt-1 text-xs text-red-500 flex flex-col">
            @if (f['phoneNo'].errors && f['phoneNo'].errors['required']) {
            <span>Phone number is required</span>
            }
            @if (f['phoneNo'].errors && f['phoneNo'].errors['pattern']) {
            <span>That phone number doesn't look right</span>
            }
          </div>
          }
        </div>
        <div>
          <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Email *</label>
          <input [ngClass]="inputCls('email')" name="del-email" type="text" placeholder="name@example.com" formControlName="email" trimInput />
          @if (f['email'].invalid && (f['email'].dirty || f['email'].touched)) {
          <div class="mt-1 text-xs text-red-500 flex flex-col">
            @if (f['email'].errors && f['email'].errors['required']) {
            <span>Email is required</span>
            }
            @if (f['email'].errors && f['email'].errors['email']) {
            <span>That email address doesn't look right</span>
            }
          </div>
          }
        </div>
        </div>
      </div>

      <!-- Address -->
      <div>
        <div class="flex items-center gap-2 mb-3">
          <mat-icon class="!text-gray-400 !text-[20px] !h-5 !w-5">home</mat-icon>
          <span class="font-semibold text-gray-900">Address</span>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4">
        <div>
          <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Street address *</label>
          <input [ngClass]="inputCls('streetAddressLineOne')" name="del-street-address-one" type="text" placeholder="House number and street name" formControlName="streetAddressLineOne" trimInput />
          @if (f['streetAddressLineOne'].invalid && (f['streetAddressLineOne'].dirty || f['streetAddressLineOne'].touched)) {
          <div class="mt-1 text-xs text-red-500 flex flex-col">
            @if (f['streetAddressLineOne'].errors && f['streetAddressLineOne'].errors['required']) {
            <span>Street name is required</span>
            }
            @if (f['streetAddressLineOne'].errors && f['streetAddressLineOne'].errors['minlength']) {
            <span>At least 5 characters</span>
            }
            @if (f['streetAddressLineOne'].errors && f['streetAddressLineOne'].errors['maxlength']) {
            <span>No more than 200 characters</span>
            }
          </div>
          }
        </div>
        <div>
          <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Flat, unit or building</label>
          <input [ngClass]="inputCls('streetAddressLineTwo')" name="del-street-address-two" type="text" placeholder="Apartment, suite, unit, etc. (optional)" formControlName="streetAddressLineTwo" trimInput />
          @if (f['streetAddressLineTwo'].invalid && (f['streetAddressLineTwo'].dirty || f['streetAddressLineTwo'].touched)) {
          <div class="mt-1 text-xs text-red-500 flex flex-col">
            @if (f['streetAddressLineTwo'].errors && f['streetAddressLineTwo'].errors['minlength']) {
            <span>At least 5 characters, or leave it empty</span>
            }
            @if (f['streetAddressLineTwo'].errors && f['streetAddressLineTwo'].errors['maxlength']) {
            <span>No more than 200 characters</span>
            }
          </div>
          }
        </div>
        <div>
          <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Town / city *</label>
          <input [ngClass]="inputCls('town')" name="del-city" type="text" placeholder="Town or city" formControlName="town" trimInput />
          @if (f['town'].invalid && (f['town'].dirty || f['town'].touched)) {
          <div class="mt-1 text-xs text-red-500 flex flex-col">
            @if (f['town'].errors && f['town'].errors['required']) {
            <span>Town or city is required</span>
            }
            @if (f['town'].errors && f['town'].errors['minlength']) {
            <span>At least 3 characters</span>
            }
            @if (f['town'].errors && f['town'].errors['maxlength']) {
            <span>No more than 200 characters</span>
            }
          </div>
          }
        </div>
        <div>
          <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">County</label>
          <input [ngClass]="inputCls('country')" name="del-country" type="text" placeholder="County (optional)" formControlName="country" trimInput />
          @if (f['country'].invalid && (f['country'].dirty || f['country'].touched)) {
          <div class="mt-1 text-xs text-red-500 flex flex-col">
            @if (f['country'].errors && f['country'].errors['minlength']) {
            <span>At least 2 characters</span>
            }
            @if (f['country'].errors && f['country'].errors['maxlength']) {
            <span>No more than 200 characters</span>
            }
          </div>
          }
        </div>
        <div>
          <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Postcode *</label>
          <input [ngClass]="inputCls('postCode')" name="del-postcode" type="text" placeholder="e.g. CO6 1EP" formControlName="postCode" trimInput style="text-transform: uppercase;" />
          @if (f['postCode'].invalid && (f['postCode'].dirty || f['postCode'].touched)) {
          <div class="mt-1 text-xs text-red-500 flex flex-col">
            @if (f['postCode'].errors && f['postCode'].errors['required']) {
            <span>Postcode is required</span>
            }
            @if (f['postCode'].errors && f['postCode'].errors['minlength']) {
            <span>At least 2 characters</span>
            }
            @if (f['postCode'].errors && f['postCode'].errors['maxlength']) {
            <span>No more than 20 characters</span>
            }
          </div>
          }
        </div>
        <div>
          <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Country / region</label>
          <div class="flex items-center gap-2 w-full px-3.5 py-2.5 rounded-xl border border-gray-200 bg-gray-100 text-sm text-gray-500">
            <mat-icon class="!text-[18px] !h-[18px] !w-[18px] !text-gray-400">flag</mat-icon> United Kingdom (UK)
          </div>
        </div>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div class="sticky bottom-0 flex items-center justify-between gap-3 px-6 py-4 bg-gray-50 border-t border-gray-100">
      <span class="text-xs font-medium" [ngClass]="createAddressForm.valid ? 'text-emerald-600' : 'text-gray-400'">
        @if (createAddressForm.valid) { Everything looks good } @else { {{ problemCount }} field{{ problemCount === 1 ? '' : 's' }} still need{{ problemCount === 1 ? 's' : '' }} attention }
      </span>
      <div class="flex items-center gap-2">
        <button type="button" class="px-5 py-2.5 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-200 transition-colors" (click)="dialogRef.close({status: false})">Cancel</button>
        <button type="submit" [disabled]="!createAddressForm.valid"
          class="flex items-center gap-1.5 px-6 py-2.5 rounded-xl bg-gradient-to-b from-amber-400 to-amber-500 text-neutral-900 text-sm font-bold shadow-sm hover:from-amber-300 hover:to-amber-400 active:scale-[0.98] transition-all disabled:opacity-40 disabled:cursor-not-allowed">
          <mat-icon class="!text-[18px] !h-[18px] !w-[18px]">{{ data.isEdit ? 'save' : 'add' }}</mat-icon>
          {{ data.isEdit ? 'Save changes' : 'Add address' }}
        </button>
      </div>
    </div>
  </form>
</mat-dialog-content>
